//codex-- Page-scoped presentation based on the matching asmainframe toolbar reference.
import React, { createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowPathIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import './PageToolbar.css';

//codex-- Keep Clear and existing Filter actions together without changing their callbacks.
const FooterContext = createContext(null);
export default function PageToolbar({ title, actions, children, footer, compact = false, className = '', ...props }) {
  const [footerNode, setFooterNode] = useState(null);
  return (
    <FooterContext.Provider value={footerNode}>
      <section {...props} aria-label={title} className={`page-toolbar ${compact ? 'page-toolbar-compact' : ''} ${className}`}>
        <div className="page-toolbar-heading">
          <p>{title}</p>
          {actions && <div className="page-toolbar-actions" aria-label={`${title} actions`}>{actions}</div>}
        </div>
        {children && <div className="page-toolbar-body">{children}</div>}
        <div ref={setFooterNode} role="group" aria-label={`${title} filter actions`} className="page-toolbar-footer">{footer}</div>
      </section>
    </FooterContext.Provider>
  );
}

//codex-- Implicit labels preserve the IDs, refs, values, and handlers of the original controls.
export function ToolbarField({ label, children }) {
  return <label className="page-toolbar-field"><span>{label}</span>{children}</label>;
}

//codex-- Portal only non-submit toolbar actions; keep form submit controls inside their original forms.
export function ToolbarFooterAction({ children }) {
  const target = useContext(FooterContext);
  return target ? createPortal(children, target) : children;
}

//codex-- Allow selected pages to place Clear beside search while preserving its reset callback and default footer placement.
export function ToolbarClear({ onClick, inline = false }) {
  const button = <button type="button" onClick={onClick || (() => window.location.reload())} className={`bg-orange-600${inline ? '' : ' page-toolbar-clear'}`}><ArrowPathIcon aria-hidden="true" className="h-4 w-4" />Clear</button>;
  return inline ? button : <ToolbarFooterAction>{button}</ToolbarFooterAction>;
}

//codex-- Retain the original Enter callback contract and make inline Clear placement opt-in.
export function PageToolbarSearch({ search, handleChange, onClick, onReset, label = 'Search', placeholder = 'Enter a search term', enterArgument = true, inlineClear = false }) {
  return (
    <div className="page-toolbar-search">
      <ToolbarField label={label}>
        <div className="page-toolbar-search-input">
          <MagnifyingGlassIcon aria-hidden="true" />
          <input type="text" name="search" value={search} onChange={handleChange} required={enterArgument}
            placeholder={placeholder} onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              if (enterArgument) onClick?.(handleChange);
              else { event.preventDefault(); onClick?.(); }
            }} />
        </div>
      </ToolbarField>
      {/* //codex-- Keep Clear in the search row only for pages that request inline placement. */}
      <ToolbarClear onClick={onReset} inline={inlineClear} />
    </div>
  );
}

//codex-- The holds page keeps its original no-argument Enter and custom reset behavior.
export function ToolbarHoldSearch({ value, onChange, onSearch, onReset, loading, placeholder }) {
  return <PageToolbarSearch search={value} handleChange={onChange} onClick={onSearch} onReset={onReset}
    loading={loading} placeholder={placeholder} label="Search holds" enterArgument={false} />;
}

//codex-- Retain WIP search criteria, typing callbacks, and five-entry history from the existing widget.
export function ToolbarDropdownSearch({ filterOptions = ['All', 'Active', 'Job Site', 'PM'], defaultFilter = 'All', placeholder = 'Enter a search term', onSearch, onTyping, onReset }) {
  const [term, setTerm] = useState('');
  const [filter, setFilter] = useState(defaultFilter);
  const [history, setHistory] = useState(() => {
    const saved = localStorage.getItem('searchHistory');
    return saved ? JSON.parse(saved) : [];
  });
  const search = () => {
    onSearch?.(term, filter);
    if (!term.trim()) return;
    const updated = [term, ...history.filter((entry) => entry !== term)].slice(0, 5);
    setHistory(updated);
    localStorage.setItem('searchHistory', JSON.stringify(updated));
  };
  const clear = () => {
    setTerm('');
    setFilter(defaultFilter);
    setHistory([]);
    onReset?.();
    localStorage.removeItem('searchHistory');
  };
  return (
    <div className="page-toolbar-dropdown-search">
      <div className="page-toolbar-row">
        <ToolbarField label="Search WIP projects"><input type="text" value={term} placeholder={placeholder}
          onChange={(event) => { setTerm(event.target.value); onTyping?.(event.target.value, filter); }}
          onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); search(); } }} /></ToolbarField>
        <ToolbarField label="Search in"><select value={filter}
          onChange={(event) => { setFilter(event.target.value); onTyping?.(term, event.target.value); }}>
          {filterOptions.map((option) => <option key={option} value={option}>{option}</option>)}
        </select></ToolbarField>
      </div>
      <ToolbarClear onClick={clear} />
      {history.length > 0 && (
        <div className="page-toolbar-history" aria-label="Recent searches">
          <span>Recent</span>
          {history.map((entry, index) => <button type="button" key={index} onClick={() => { setTerm(entry); onSearch?.(entry, filter); }}>{entry}</button>)}
        </div>
      )}
    </div>
  );
}
